import { type Json, type Option, WireDecodeError } from "@tento-lona/sheets";
import { Model } from "@tento-core/model";
import { TypedApi } from "@tento-core/typed-api";
import { Network } from "../../network.ts";
import { SdkRequestError } from "../../errors.ts";
import type { ClientLifecycle } from "../../client/handle-errors.ts";

/** Which payments environment a product-key or customer read addresses: `live` or `test`. */
export type PaymentsMode = "live" | "test";

/** `UserClient.listProductKeys` filters. Every field is required; `null`
 *  leaves that filter off (start from `ListProductKeysOptions.DEFAULT`). */
export interface ListProductKeysOptions {
  page: Option<number>;
  pageSize: Option<number>;
  active: Option<boolean>;
  expired: Option<boolean>;
}

/** Values that go with `ListProductKeysOptions`: `DEFAULT`. */
export namespace ListProductKeysOptions {
  /** No filter: the server's first page at its default size. */
  export const DEFAULT: ListProductKeysOptions = {
    page: null,
    pageSize: null,
    active: null,
    expired: null,
  };
}


/**
 * Viewer-scoped user reads. Wraps the `/api/user/*` route surface
 * — current user info, OAuth credential listing, payments customer
 * id, product key pagination. Every read is checked: a rejected or
 * unanswered request throws `SdkRequestError` (boundary `user`).
 */
export class UserClient {
  /** The owning client's liveness: every read checks it first. */
  readonly #lifecycle: ClientLifecycle;

  /** `lifecycle`: the owning client's (`LonaSdk.Client.Lifecycle`); after
   *  its `dispose` every read rejects with `LonaSdk.Client.DisposedError`. */
  #api: TypedApi<Network.Routes>;
  constructor(api: TypedApi<Network.Routes>, lifecycle: ClientLifecycle) {
    this.#api = api;
    this.#lifecycle = lifecycle;
  }

  /**
   * Active viewer + embedded auths in `AuthDisplay` shape. Throws
   * `SdkRequestError` (boundary `user`, step `get`) when `GET /api/user` is
   * rejected (e.g. signed out) or unanswered.
   */
  async get(): Promise<Network.UserWithAuthDisplay> {
    this.#lifecycle.assertLive("user.get");
    return SdkRequestError.checkRequest(
      "user",
      "get",
      "GET",
      "/api/user",
      () => this.#api.getResult("/api/user"),
    );
  }

  /**
   * List the active viewer's auth credentials in the richer
   * `AuthInfoResponse` shape (issuedAt, expiresAt,
   * refreshExpiresAt, requiresLogin, scopes-as-array). Use this
   * when [`get`]'s embedded `AuthDisplay` summary isn't enough.
   *
   * [`get`]: UserClient.get
   */
  async listAuths(): Promise<Network.AuthInfoResponse[]> {
    this.#lifecycle.assertLive("user.listAuths");
    return SdkRequestError.checkRequest(
      "user",
      "list-auths",
      "GET",
      "/api/user/auths",
      () => this.#api.getResult("/api/user/auths"),
    );
  }

  /**
   * Resolve the active viewer's Stripe customer id for `mode` (`"live"`
   * or `"test"` — named at every call, never defaulted). Lazy-creates a
   * Stripe customer record server-side on first call.
   */
  async getCustomerId(mode: PaymentsMode): Promise<string> {
    this.#lifecycle.assertLive("user.getCustomerId");
    return SdkRequestError.checkRequest(
      "user",
      "get-customer-id",
      "GET",
      "/api/user/payments/customer-id",
      async () =>
        (await this.#api.getResult("/api/user/payments/customer-id", { query: { mode } }))
          .map(customerIdOf),
    );
  }

  /**
   * Paginated list of the active viewer's product keys with
   * filter knobs (`{}` for none). Unset fields take the server
   * defaults: page 1, pageSize 25.
   */
  async listProductKeys(
    opts: ListProductKeysOptions,
  ): Promise<Network.PaginatedProductKeysResponse> {
    this.#lifecycle.assertLive("user.listProductKeys");
    return SdkRequestError.checkRequest(
      "user",
      "list-product-keys",
      "GET",
      "/api/user/subscription/product-keys",
      () =>
        this.#api.getResult("/api/user/subscription/product-keys", {
          query: {
            page: opts.page ?? null,
            pageSize: opts.pageSize ?? null,
            active: opts.active ?? null,
            expired: opts.expired ?? null,
            createdAfter: null,
            createdBefore: null,
            expiresAfter: null,
            expiresBefore: null,
          },
        }),
    );
  }
}

/**
 * The customer-id route answers a bare JSON string, which the generated route
 * map does not declare (it declares no response): anything else is malformed.
 */
function customerIdOf(raw: Json): string {
  if (typeof raw === "string") return raw;
  throw new WireDecodeError("CustomerId", [Model.issue([], "type", "expected string")]);
}
